import dayjs from "dayjs";
import { z } from "zod";
import { formatStringToEuropeanCurrency, parseNettoPrice } from "../products/helper";
const REFERENCE_NUMBER_REGEX = /^[a-zA-Z0-9\s.,:;]+$/;
export const isServiceDateValueSet = (value)=>{
    if (value === null || value === undefined || value === "") return false;
    return dayjs(value).isValid();
};
const parseLocaleNumber = (value)=>{
    const str = (value !== null && value !== void 0 ? value : "").toString().trim();
    if (!str) return NaN;
    return parseFloat(str.replace(",", "."));
};
export const parsePaymentTermsToDays = (value)=>{
    const raw = (value !== null && value !== void 0 ? value : "").toString().trim().toLowerCase();
    if (!raw) return null;
    if (raw === "immediately" || raw === "sofort") return 0;
    const amount = parseLocaleNumber(raw);
    if (!Number.isFinite(amount)) return null;
    if (raw.includes("month") || raw.includes("monat")) return Math.floor(amount * 30);
    if (raw.includes("day") || raw.includes("tag")) return Math.floor(amount);
    return null;
};
export const sendTypeOptions = (t)=>[
        {
            label: t("email"),
            value: "email"
        },
        {
            label: t("download"),
            value: "download"
        }
    ];
export const paymentMethods = (lng)=>[
        {
            label: lng === "en" ? "Cash" : "Kasse",
            value: "cash"
        },
        {
            label: lng === "en" ? "Bank Transfer" : "Bank\xfcberweisung",
            value: "bank transfer"
        },
        {
            label: lng === "en" ? "Discount" : "Skonto",
            value: "skonto"
        },
        {
            label: lng === "en" ? "Others" : "Andere",
            value: "others"
        }
    ];
export const paymentTerms = (lng)=>[
        {
            label: lng === "en" ? "Immediately" : "Sofort",
            value: "Immediately"
        },
        {
            label: lng === "en" ? "7 days" : "7 Tage",
            value: "7 days"
        },
        {
            label: lng === "en" ? "10 days" : "10 Tage",
            value: "10 days"
        },
        {
            label: lng === "en" ? "14 days" : "14 Tage",
            value: "14 days"
        },
        {
            label: lng === "en" ? "1 month" : "1 Monat",
            value: "1 month"
        }
    ];
export const productDefaultValue = {
    "productId": {},
    "lineType": "catalog",
    "description": "",
    "quantity": "",
    "unit": "",
    "vat": "",
    "unitPrice": "",
    "total": "",
    "productName": ""
};
export const invoiceFormDefaultValues = {
    "items": [
        productDefaultValue
    ],
    "customerId": {},
    "documentType": "invoice",
    "invoiceLanguage": "",
    "invoiceDate": dayjs(),
    "servicePlace": "",
    "serviceStartDate": dayjs(),
    "serviceEndDate": dayjs(),
    "referenceNumber": "",
    "invoiceTotalAmount": "",
    "invoiceAmountExclVat": "",
    "vat": "0",
    "paymentMethod": "",
    "paymentTerms": "",
    "discount": "",
    "discountDays": "7",
    "discountPercent": "2",
    "additionalText1": "",
    "additionalText2": "",
    "invoiceMessage": "",
    "message19": "",
    "message4": "",
    "message13b": "",
    "message25a": ""
};
const ProductSchema = z.object({
    itemId: z.number().optional().or(z.null()),
    // In the form, `productId` should be the full option object (from Autocomplete),
    // but some flows may temporarily set it to a raw string id. Accept both.
    productId: z.union([
        z.object({
            product_id: z.any().optional(),
            product_name: z.any().optional(),
            label: z.any().optional(),
            value: z.any().optional()
        }),
        z.string()
    ]).optional().or(z.null()),
    description: z.string().optional().or(z.literal("")),
    productName: z.string().optional().or(z.literal("")),
    quantity: z.string(),
    unit: z.string(),
    vat: z.string(),
    unitPrice: z.string(),
    lineType: z.string().optional().or(z.literal("")),
    productType: z.string().optional().or(z.literal(""))
});
const ItemSchema = z.object({
    itemId: z.number().optional().or(z.null()),
    productId: z.union([
        z.object({
            product_id: z.any().optional(),
            product_name: z.any().optional(),
            label: z.any().optional(),
            value: z.any().optional()
        }),
        z.string()
    ]).optional().or(z.null()),
    productName: z.string().optional().or(z.literal("")),
    description: z.string().optional().or(z.literal("")),
    quantity: z.string(),
    unit: z.string(),
    vat: z.string(),
    unitPrice: z.string(),
    lineType: z.string().optional().or(z.literal(""))
});
export const InvoiceSchema = (t, getRequireServiceDates)=>z.object({
        invoiceId: z.string().optional(),
        customerId: z.object({
            customer_id: z.string().optional(),
            tax_ust_id_number: z.any().optional(),
            tax_number: z.any().optional()
        }).refine((val)=>{
            return val && typeof val === "object" && val.customer_id && String(val.customer_id).trim().length > 0;
        }, {
            message: t("selectCustomerToProceed")
        }),
        documentType: z.string().trim(),
        invoiceLanguage: z.string(),
        invoiceDate: z.any(),
        serviceStartDate: z.any(),
        serviceEndDate: z.any(),
        servicePlace: z.string().regex(/^[a-zA-Z0-9\s]+$/, {
            message: t("servicePlaceInvalid")
        }).optional().or(z.literal("")),
        referenceNumber: z.union([
            z.literal(""),
            z.null(),
            z.string().regex(REFERENCE_NUMBER_REGEX, {
                message: t("referenceNumberInvalid")
            })
        ]).optional(),
        paymentMethod: z.string(),
        paymentTerms: z.string(),
        discount: z.string().trim(),
        discountDays: z.string().optional().or(z.literal("")).refine((v)=>{
            if (!v || v.toString().trim() === "") return true;
            const n = parseLocaleNumber(v);
            return Number.isFinite(n) && n >= 1;
        }, {
            message: t("discountDaysMustBePositive")
        }),
        discountPercent: z.string().optional().or(z.literal("")).refine((v)=>{
            if (!v || v.toString().trim() === "") return true;
            const n = parseLocaleNumber(v);
            return Number.isFinite(n) && n > 0 && n <= 100;
        }, {
            message: t("discountPercentMustBePositive")
        }),
        additionalText1: z.string().trim(),
        additionalText2: z.string().trim(),
        invoiceMessage: z.string().trim(),
        message19: z.string().trim().optional(),
        message4: z.string().trim().optional(),
        message13b: z.string().trim().optional(),
        message25a: z.string().trim().optional(),
        items: z.array(ProductSchema).superRefine((data, ctx)=>{
            data.forEach((item, index)=>{
                // Check if product is selected (either productId with product_id OR productName exists)
                const hasProductId = Boolean(item.productId) && (typeof item.productId === "string" && item.productId.trim().length > 0 || typeof item.productId === "object" && item.productId.product_id && String(item.productId.product_id).trim().length > 0);
                const hasProductName = item.productName && String(item.productName).trim().length > 0;
                const hasProduct = hasProductId || hasProductName;
                if (hasProduct) {
                    const fieldMessages = {
                        unit: t("unitRequired"),
                        vat: t("taxRequired"),
                        unitPrice: t("netPriceRequired")
                    };
                    const fields = [
                        "unit",
                        "vat",
                        "unitPrice"
                    ];
                    fields.forEach((field)=>{
                        const fieldValue = item[field];
                        if (!fieldValue || typeof fieldValue === "string" && fieldValue.trim().length < 1) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                path: [
                                    index,
                                    field
                                ],
                                message: fieldMessages[field]
                            });
                        }
                    });
                    // Validate quantity separately to ensure it's positive and greater than zero
                    const quantityValue = item.quantity;
                    if (!quantityValue || typeof quantityValue === "string" && quantityValue.trim().length < 1) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: [
                                index,
                                "quantity"
                            ],
                            message: t("quantityRequired")
                        });
                    } else {
                        const quantityNum = parseFloat(String(quantityValue));
                        if (isNaN(quantityNum) || quantityNum <= 0) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                path: [
                                    index,
                                    "quantity"
                                ],
                                message: t("quantityMustBePositive")
                            });
                        }
                    }
                } else {
                    // If no product is selected, show error on productId field
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: [
                            index,
                            "productId"
                        ],
                        message: t("selectProductToProceed")
                    });
                }
            });
        }),
        vat: z.string()
    }).superRefine((data, ctx)=>{
        var _getRequireServiceDates;
        const requireServiceDates = (_getRequireServiceDates = getRequireServiceDates === null || getRequireServiceDates === void 0 ? void 0 : getRequireServiceDates()) !== null && _getRequireServiceDates !== void 0 ? _getRequireServiceDates : false;
        if (requireServiceDates) {
            if (!isServiceDateValueSet(data.serviceStartDate)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [
                        "serviceStartDate"
                    ],
                    message: t("serviceStartDateRequired")
                });
            }
            if (!isServiceDateValueSet(data.serviceEndDate)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [
                        "serviceEndDate"
                    ],
                    message: t("serviceEndDateRequired")
                });
            }
        }
        if (isServiceDateValueSet(data.serviceStartDate) && isServiceDateValueSet(data.serviceEndDate) && dayjs(data.serviceStartDate).diff(dayjs(data.serviceEndDate)) > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.invalid_date,
                path: [
                    "serviceEndDate"
                ],
                message: t("servicePeriodError")
            });
        }
        const paymentTermDays = parsePaymentTermsToDays(data.paymentTerms);
        var _data_discount;
        const isDiscountActive = Boolean(((_data_discount = data.discount) !== null && _data_discount !== void 0 ? _data_discount : "").toString().trim());
        const discountDays = parseLocaleNumber(data.discountDays);
        if (isDiscountActive && paymentTermDays !== null && paymentTermDays > 0 && Number.isFinite(discountDays) && discountDays > paymentTermDays) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [
                    "discountDays"
                ],
                message: t("discountDaysWithinPaymentTerms")
            });
        }
    });
// Helper function to parse VAT value (handles special tax rates like 0_13b, 0_25a)
export const calculateInvoiceTotals = function() {
    let items = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : [];
    let invoiceAmountExclVat = 0;
    let vatTotal = 0;
    if (!Array.isArray(items)) {
        return {
            invoiceAmountExclVat: 0,
            invoiceTotalAmount: 0
        };
    }
    for (const item of items){
        var _item_unitPrice, _ref;
        const rawUnitPrice = (_ref = (_item_unitPrice = item === null || item === void 0 ? void 0 : item.unitPrice) !== null && _item_unitPrice !== void 0 ? _item_unitPrice : item === null || item === void 0 ? void 0 : item.unit_price) !== null && _ref !== void 0 ? _ref : "0";
        const unitPrice = typeof rawUnitPrice === "number" ? rawUnitPrice : parseNettoPrice(formatStringToEuropeanCurrency(String(rawUnitPrice)));
        var _item_quantity;
        const quantity = parseFloat(String((_item_quantity = item === null || item === void 0 ? void 0 : item.quantity) !== null && _item_quantity !== void 0 ? _item_quantity : "0").replace(",", "."));
        if (!Number.isFinite(unitPrice) || !Number.isFinite(quantity)) continue;
        const lineNet = unitPrice * quantity;
        invoiceAmountExclVat += lineNet;
        vatTotal += lineNet * (parseVatValue(item === null || item === void 0 ? void 0 : item.vat) / 100);
    }
    return {
        invoiceAmountExclVat,
        invoiceTotalAmount: invoiceAmountExclVat + vatTotal
    };
};
export const parseVatValue = (vat)=>{
    const vatStr = (vat === null || vat === void 0 ? void 0 : vat.toString()) || "0";
    // Extract numeric value: 0_13b -> 0, 0_25a -> 0, 19 -> 19, etc.
    const numericPart = vatStr.split("_")[0];
    return parseFloat(numericPart) || 0;
};
// Helper function to combine tax_rate and vat_type for display
export const combineVatDisplay = (taxRate, vatType)=>{
    if (!vatType) {
        return taxRate.toString();
    }
    return "".concat(taxRate, "_").concat(vatType);
};
export const resolveProductTaxRateValue = (taxRate, vatType, hasVatExemption)=>{
    if (hasVatExemption) return "0";
    return combineVatDisplay(taxRate !== null && taxRate !== void 0 ? taxRate : "0", vatType !== null && vatType !== void 0 ? vatType : null);
};
export const resolveCatalogProductFromItem = (item, products)=>{
    const productId = item === null || item === void 0 ? void 0 : item.productId;
    if (productId && typeof productId === "object" && productId.tax_rate != null) {
        return productId;
    }
    const id = typeof productId === "object" ? productId === null || productId === void 0 ? void 0 : productId.product_id : productId;
    if (!id) return null;
    var _products_find;
    return (_products_find = products.find((p)=>String(p.product_id) === String(id))) !== null && _products_find !== void 0 ? _products_find : null;
};
export const normalizeItemsVatForCompanySettings = function(items, hasVatExemption) {
    let products = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : [];
    return items.map((item)=>{
        var _item_vat;
        const vatStr = ((_item_vat = item.vat) !== null && _item_vat !== void 0 ? _item_vat : "").toString();
        const isOneTime = item.lineType === "one_time" || item.productType === "one-time";
        if (hasVatExemption) {
            if (vatStr === "0" && !item.vatType) return item;
            return {
                ...item,
                vat: "0",
                vatType: null
            };
        }
        if (vatStr !== "0" || item.vatType) return item;
        if (isOneTime) {
            return {
                ...item,
                vat: "19",
                vatType: null
            };
        }
        const product = resolveCatalogProductFromItem(item, products);
        if (!product) return item;
        const productRate = Number(product.tax_rate);
        if (!Number.isFinite(productRate) || productRate === 0) return item;
        return {
            ...item,
            vat: resolveProductTaxRateValue(product.tax_rate, product.vat_type, false),
            vatType: product.vat_type || null
        };
    });
};
export const hasItemsVatChangedForCompanySettings = (originalItems, normalizedItems)=>{
    if (originalItems.length !== normalizedItems.length) return true;
    return originalItems.some((original, index)=>{
        const normalized = normalizedItems[index];
        var _original_vat;
        const originalVat = ((_original_vat = original === null || original === void 0 ? void 0 : original.vat) !== null && _original_vat !== void 0 ? _original_vat : "").toString();
        var _normalized_vat;
        const normalizedVat = ((_normalized_vat = normalized === null || normalized === void 0 ? void 0 : normalized.vat) !== null && _normalized_vat !== void 0 ? _normalized_vat : "").toString();
        var _original_vatType;
        const originalVatType = (_original_vatType = original === null || original === void 0 ? void 0 : original.vatType) !== null && _original_vatType !== void 0 ? _original_vatType : null;
        var _normalized_vatType;
        const normalizedVatType = (_normalized_vatType = normalized === null || normalized === void 0 ? void 0 : normalized.vatType) !== null && _normalized_vatType !== void 0 ? _normalized_vatType : null;
        return originalVat !== normalizedVat || originalVatType !== normalizedVatType;
    });
};
// Helper function to extract tax_rate and vat_type from combined value
export const extractVatComponents = (combinedVat)=>{
    const parts = combinedVat.split("_");
    if (parts.length === 1) {
        // Regular tax rate (no special type)
        return {
            tax_rate: parseInt(parts[0]),
            vat_type: null
        };
    }
    // Special tax rate with type (e.g., 0_13b, 0_25a)
    return {
        tax_rate: parseInt(parts[0]),
        vat_type: parts[1]
    };
};
export const keysToCheckEquality = [
    "invoiceId",
    "customerId",
    "documentType",
    "invoiceLanguage",
    "invoiceDate",
    "serviceStartDate",
    "serviceEndDate",
    "servicePlace",
    "paymentMethod",
    "paymentTerms",
    "discount",
    "discountDays",
    "discountPercent",
    "discountDaysNumber",
    "discountPercentNumber",
    "discountedTotalAmount",
    "additionalText1",
    "additionalText2",
    "invoiceMessage",
    "message19",
    "message4",
    "message13b",
    "message25a",
    "items",
    "invoiceAmountExclVat",
    "invoiceTotalAmount",
    "referenceNumber"
];
export const productKeysToCheckEquality = [
    "itemId",
    "productId",
    "lineType",
    "description",
    "quantity",
    "unit",
    "vat",
    "unitPrice",
    "total"
];
export const constructPayload = (values)=>{
    values.invoiceDate = dayjs(values.invoiceDate).format("YYYY-MM-DD");
    values.serviceStartDate = dayjs(values.serviceDate ? values.serviceDate[0] : values.serviceStartDate).format("YYYY-MM-DD");
    values.serviceEndDate = dayjs(values.serviceDate ? values.serviceDate[1] : values.serviceEndDate).format("YYYY-MM-DD");
    const customer = {
        ...values.customerId
    };
    // Keep the VAT value from the form (default VAT percentage from invoice settings)
    // Don't parse customer tax IDs as VAT - they are identification numbers, not percentages
    if (typeof values.vat === "string" && values.vat !== "") {
        values.vat = parseFloat(values.vat) || 0;
    } else {
        values.vat = 0;
    }
    values.customerId = customer.customer_id;
    const normalizeProductId = (productId)=>{
        const raw = typeof productId === "string" ? productId : productId && productId.product_id ? productId.product_id : null;
        if (raw === null || raw === undefined) return null;
        const text = String(raw).trim().toLowerCase();
        if (!text || text === "null" || text === "undefined" || text === "0") return null;
        return text;
    };
    const isOneTimeItem = (item)=>item.lineType === "one_time" || item.productType === "one-time";
    const buildCatalogItem = (item)=>{
        var _item_productId, _item_productId1;
        const resolvedProductId = normalizeProductId(item.productId);
        if (!resolvedProductId) return null;
        const vatComponents = extractVatComponents(item.vat);
        const vatValue = vatComponents.vat_type ? "".concat(vatComponents.tax_rate, "_").concat(vatComponents.vat_type) : vatComponents.tax_rate.toString();
        return {
            itemId: item.itemId,
            productId: resolvedProductId,
            lineType: "catalog",
            unitPrice: parseNettoPrice(formatStringToEuropeanCurrency(item.unitPrice)),
            description: item.description || "",
            quantity: parseFloat(item.quantity),
            unit: item.unit,
            total: parseNettoPrice(formatStringToEuropeanCurrency(item.unitPrice)) * parseFloat(item.quantity),
            vat: vatValue,
            vatType: vatComponents.vat_type,
            productName: (typeof item.productId === "object" && item.productId ? ((_item_productId = item.productId) === null || _item_productId === void 0 ? void 0 : _item_productId.product_name) || ((_item_productId1 = item.productId) === null || _item_productId1 === void 0 ? void 0 : _item_productId1.label) : "") || item.productName || ""
        };
    };
    const buildOneTimeItem = (item)=>{
        const vatComponents = extractVatComponents(item.vat);
        const vatValue = vatComponents.vat_type ? "".concat(vatComponents.tax_rate, "_").concat(vatComponents.vat_type) : vatComponents.tax_rate.toString();
        return {
            itemId: item.itemId,
            productId: null,
            lineType: "one_time",
            productName: item.productName || "",
            productType: item.productType,
            unitPrice: parseNettoPrice(formatStringToEuropeanCurrency(item.unitPrice)),
            description: item.description || "",
            quantity: parseFloat(item.quantity),
            unit: item.unit,
            total: parseNettoPrice(formatStringToEuropeanCurrency(item.unitPrice)) * parseFloat(item.quantity),
            vat: vatValue,
            vatType: vatComponents.vat_type
        };
    };
    // Preserve the user's item order (catalog and one-time lines may be interleaved).
    values.items = values.items.map((item)=>isOneTimeItem(item) ? buildOneTimeItem(item) : buildCatalogItem(item)).filter((item)=>Boolean(item));
    const totals = calculateInvoiceTotals(values.items);
    values.invoiceAmountExclVat = totals.invoiceAmountExclVat;
    values.invoiceTotalAmount = totals.invoiceTotalAmount;
    // Derived discount fields (stored in backend for stable PDF rendering / auditing)
    // Keep original `discountDays` / `discountPercent` as user-entered strings,
    // but also send numeric versions and the discounted gross total.
    const discountDaysNumber = parseLocaleNumber(values.discountDays);
    const discountPercentNumber = parseLocaleNumber(values.discountPercent);
    values.discountDaysNumber = Number.isFinite(discountDaysNumber) ? Math.floor(discountDaysNumber) : null;
    values.discountPercentNumber = Number.isFinite(discountPercentNumber) ? discountPercentNumber : null;
    values.discountedTotalAmount = Number.isFinite(values.invoiceTotalAmount) && Number.isFinite(discountPercentNumber) && discountPercentNumber > 0 ? Number((values.invoiceTotalAmount * (1 - discountPercentNumber / 100)).toFixed(2)) : null;
    if (!values.referenceNumber) delete values.referenceNumber;
    if (values.invoiceOpenAmount) values.invoiceOpenAmount = values.invoiceTotalAmount;
    if (values.amountPaid) values.amountPaid = parseFloat(values.amountPaid);
    return values;
};
export const checkSavedState = (currValue, prevValue)=>{
    // Use JSON parse/stringify for current state to handle dayjs objects properly
    // structuredClone doesn't preserve dayjs methods which causes .clone() errors
    const currentState = constructPayload(JSON.parse(JSON.stringify(currValue)));
    const lastState = structuredClone(prevValue);
    Object.keys(currentState).forEach((key)=>{
        if (!keysToCheckEquality.includes(key)) delete currentState[key];
    });
    if (currentState.items && Array.isArray(currentState.items)) {
        currentState.items.forEach((item)=>{
            Object.keys(item).forEach((key)=>{
                if (!productKeysToCheckEquality.includes(key)) delete item[key];
            });
        });
    }
    Object.keys(lastState).forEach((key)=>{
        if (!keysToCheckEquality.includes(key)) delete lastState[key];
    });
    if (lastState.items && Array.isArray(lastState.items)) {
        lastState.items.forEach((item)=>{
            Object.keys(item).forEach((key)=>{
                if (!productKeysToCheckEquality.includes(key)) delete item[key];
            });
        });
    }
    // Normalize VAT values in items for comparison
    // Ensure VAT is consistently formatted as string and handle vatType
    const normalizeItemVat = (item)=>{
        if (item.vat !== undefined) {
            const vatStr = String(item.vat || "0").trim();
            // Check if VAT already contains vatType (e.g., "0_13b", "0_25a")
            const hasVatType = vatStr.includes("_") && (vatStr.includes("_13b") || vatStr.includes("_25a"));
            if (hasVatType) {
                // Already in combined format, just normalize the numeric part
                const parts = vatStr.split("_");
                const numericPart = parseFloat(parts[0]) || 0;
                const vatType = parts[1];
                item.vat = "".concat(numericPart, "_").concat(vatType);
            } else if (item.vatType) {
                // vatType exists separately, combine it
                const numericPart = parseFloat(vatStr) || 0;
                item.vat = "".concat(numericPart, "_").concat(item.vatType);
            } else {
                // Just numeric VAT, normalize to integer string
                const numericPart = parseFloat(vatStr) || 0;
                item.vat = String(numericPart);
            }
        }
        // Remove vatType from comparison as it's already combined in vat
        delete item.vatType;
        return item;
    };
    // Normalize VAT in both states
    if (currentState.items) {
        currentState.items = currentState.items.map(normalizeItemVat);
    }
    if (lastState.items) {
        lastState.items = lastState.items.map(normalizeItemVat);
    }
    // Normalize message fields - treat empty strings, null, and undefined as equivalent
    // This prevents false positives when these optional fields are conditionally shown
    const normalizeMessage = (value)=>{
        if (value === "" || value === null || value === undefined) {
            return "";
        }
        return value;
    };
    currentState.message19 = normalizeMessage(currentState.message19);
    lastState.message19 = normalizeMessage(lastState.message19);
    currentState.message4 = normalizeMessage(currentState.message4);
    lastState.message4 = normalizeMessage(lastState.message4);
    currentState.message13b = normalizeMessage(currentState.message13b);
    lastState.message13b = normalizeMessage(lastState.message13b);
    currentState.message25a = normalizeMessage(currentState.message25a);
    lastState.message25a = normalizeMessage(lastState.message25a);
    return JSON.stringify(currentState) === JSON.stringify(lastState);
};
export const MailFormSchema = (t)=>z.object({
        sendMethod: z.enum([
            "email",
            "download"
        ]),
        recipient: z.string().trim().min(1, {
            message: t("recipientRequired")
        }),
        document: z.string().trim().min(1, {
            message: t("documentRequired")
        }),
        message: z.string().trim().min(1, {
            message: t("messageRequired")
        })
    });
export const AddPaymentSchema = (t)=>z.object({
        paymentMethod: z.string().trim().min(1, {
            message: t("paymentMethodRequired")
        }),
        paymentDate: z.any(),
        openAmount: z.string().trim().min(1, {
            message: t("openAmountRequired")
        }),
        paidAmount: z.string().trim().min(1, {
            message: t("paidAmountRequired")
        })
    });
export const AddReminderSchema = (t)=>z.object({
        // Optional: reminder fee can be empty
        amount: z.string().trim().optional().or(z.literal("")),
        dueDate: z.any(),
        reminderDate: z.any(),
        // Optional so users can disable these texts (disabled = empty string)
        message: z.string().trim().optional().or(z.literal("")),
        text: z.string().trim().optional().or(z.literal(""))
    });
export const AddCreditSchema = (t)=>z.object({
        customer: z.string().trim().min(1, {
            message: t("openAmountRequired")
        }),
        creditNoteDate: z.any(),
        referenceNumber: z.string().optional().or(z.literal("")).or(z.null()),
        additionalText1: z.string().trim().min(1, {
            message: t("additionalTextRequired")
        }),
        invoiceMessage: z.string().trim().min(1, {
            message: t("invoiceMessageRequired")
        }),
        message19: z.string().trim().optional(),
        message4: z.string().trim().optional(),
        message13b: z.string().trim().optional(),
        message25a: z.string().trim().optional(),
        items: z.array(ItemSchema).superRefine((data, ctx)=>{
            data.forEach((item, index)=>{
                // Validate quantity even before product selection
                const quantityValue = item.quantity;
                if (!quantityValue || typeof quantityValue === "string" && quantityValue.trim().length < 1) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: [
                            index,
                            "quantity"
                        ],
                        message: t("quantityRequired")
                    });
                } else {
                    const quantityNum = parseFloat(String(quantityValue));
                    if (isNaN(quantityNum) || quantityNum <= 0) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: [
                                index,
                                "quantity"
                            ],
                            message: t("quantityMustBePositive")
                        });
                    }
                }
                const hasProductId = Boolean(item.productId) && (typeof item.productId === "string" && item.productId.trim().length > 0 || typeof item.productId === "object" && item.productId.product_id && String(item.productId.product_id).trim().length > 0);
                const hasProductName = item.productName && String(item.productName).trim().length > 0;
                const hasProduct = hasProductId || hasProductName;
                if (!hasProduct) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: [
                            index,
                            "productId"
                        ],
                        message: t("selectProductToProceed")
                    });
                    return;
                }
                const fieldMessages = {
                    unit: t("unitRequired"),
                    vat: t("taxRequired"),
                    unitPrice: t("amountRequired")
                };
                const fields = [
                    "unit",
                    "vat",
                    "unitPrice"
                ];
                fields.forEach((field)=>{
                    const value = item[field];
                    if (!value || typeof value === "string" && value.trim().length < 1) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: [
                                index,
                                field
                            ],
                            message: fieldMessages[field]
                        });
                    }
                });
            });
        })
    });
